const DRAFT_SOURCES = new Set(['new', 'template', 'remix']);
export const CLIP_WORKSPACE_DRAFT_KEY = 'musicmint:clips:workspace-draft:v1';
const DRAFT_TTL = 24 * 60 * 60 * 1000;
const BRIEF_KEYS = ['script_or_brief', 'brand_profile_id', 'platform', 'use_case', 'duration', 'mood', 'style', 'language', 'brand_or_product_name', 'needs_vertical_video', 'needs_commercial_license', 'reference_url'];
const VISUAL_TYPES = new Set(['product', 'brand_logo', 'scene', 'style', 'person']);
const VISUAL_TEXT_KEYS = ['id', 'file_id', 'oss_key', 'url', 'type', 'label', 'source'];
const first = value => String(Array.isArray(value) ? value[0] || '' : value || '').trim();

export function clipWorkspaceStorage() {
  try { return typeof window === 'undefined' ? null : window.sessionStorage; } catch { return null; }
}

/** An explicit object always wins; fresh drafts never resume a stored object. */
export function clipWorkspaceTarget(query, storedPackageId = '') {
  const packageId = first(query.package_id);
  if (packageId) return { mode: 'resume', packageId, context: '' };
  const source = first(query.source);
  if (DRAFT_SOURCES.has(source)) {
    const sourceId = first(source === 'template' ? query.template_id : query.source_package_id);
    return { mode: 'draft', packageId: '', source, sourceId, context: `${source}:${first(query.draft) || sourceId || 'legacy'}` };
  }
  return { mode: 'resume', packageId: first(storedPackageId), context: '' };
}

/** draftId is a local editor context, never a backend package or generation ID. */
export function buildClipDraftLocation(source, draftId, sourceId = '') {
  if (!DRAFT_SOURCES.has(source) || !draftId) throw new Error('Draft context missing');
  const query = new URLSearchParams({ source, draft: String(draftId) });
  if (sourceId) query.set(source === 'template' ? 'template_id' : 'source_package_id', String(sourceId));
  return `/clips?${query}`;
}

function briefSnapshot(brief) {
  if (!brief || typeof brief.script_or_brief !== 'string') return null;
  const snapshot = {};
  for (const key of BRIEF_KEYS) {
    if (!(key in brief)) continue;
    const value = brief[key];
    if (key.startsWith('needs_')) {
      if (typeof value !== 'boolean') return null;
    } else if (typeof value !== 'string') return null;
    snapshot[key] = value;
  }
  return snapshot;
}

function savedDrafts(storage) {
  const raw = storage.getItem(CLIP_WORKSPACE_DRAFT_KEY);
  if (!raw || raw.length > 30000) return [];
  const saved = JSON.parse(raw);
  if (saved.version === 2 && Array.isArray(saved.drafts)) return saved.drafts.slice(-8).filter(item => item && typeof item === 'object');
  return saved.version === 1 ? [saved] : [];
}

/** Keep uploaded-file references and selections, never file bytes or generated results. */
function visualSnapshot(assets) {
  if (!Array.isArray(assets)) return [];
  return assets.slice(0, 7).flatMap(asset => {
    if (!asset || !asset.file_id || !VISUAL_TYPES.has(asset.type) || typeof asset.use_for_mv !== 'boolean' || !Number.isFinite(asset.sort)) return [];
    const snapshot = { use_for_mv: asset.use_for_mv, sort: asset.sort };
    for (const key of VISUAL_TEXT_KEYS) {
      if (!(key in asset)) continue;
      if (typeof asset[key] !== 'string' || asset[key].length > 2048) return [];
      snapshot[key] = asset[key];
    }
    if (snapshot.url && !/^https?:\/\//i.test(snapshot.url)) return [];
    if (Number.isFinite(asset.created_at)) snapshot.created_at = asset.created_at;
    return [snapshot];
  });
}

export function readClipWorkspaceVisuals(storage, context, owner = '', now = Date.now()) {
  try {
    const saved = savedDrafts(storage).find(item => item.context === context && item.owner === owner);
    if (!saved || !Number.isFinite(saved.updated) || saved.updated > now || now - saved.updated > DRAFT_TTL || !briefSnapshot(saved.brief)) return [];
    return visualSnapshot(saved.visual_assets);
  } catch { return []; }
}

export function readClipWorkspaceDraft(storage, context, owner = '', now = Date.now()) {
  try {
    const saved = savedDrafts(storage).find(item => item.context === context && item.owner === owner);
    if (!saved || !Number.isFinite(saved.updated) || saved.updated > now || now - saved.updated > DRAFT_TTL) return null;
    return briefSnapshot(saved.brief);
  } catch { return null; }
}

export function saveClipWorkspaceDraft(storage, context, owner, brief, now = Date.now(), visualAssets = []) {
  const snapshot = briefSnapshot(brief);
  if (!context || !snapshot) return false;
  try {
    let previous = [];
    try { previous = savedDrafts(storage); } catch { /* discard malformed local cache */ }
    const drafts = previous.filter(item => item.context !== context || item.owner !== owner)
      .filter(item => typeof item.context === 'string' && typeof item.owner === 'string' && Number.isFinite(item.updated) && item.updated <= now && now - item.updated <= DRAFT_TTL && briefSnapshot(item.brief));
    drafts.push({ context, owner, updated: now, brief: snapshot, visual_assets: visualSnapshot(visualAssets) });
    while (drafts.length > 8) drafts.shift();
    let raw = JSON.stringify({ version: 2, drafts });
    while (raw.length > 30000 && drafts.length > 1) { drafts.shift(); raw = JSON.stringify({ version: 2, drafts }); }
    if (raw.length > 30000) return false;
    storage.setItem(CLIP_WORKSPACE_DRAFT_KEY, raw);
    return true;
  } catch { return false; }
}
